import { Color, Spacing, Typography } from '@persona-id/design-tokens';
import { InputCheckIcon, InputDashIcon } from '@persona-id/design-tokens/icons';
import { uniqueId } from 'lodash';
import {
  ChangeEvent,
  ComponentPropsWithoutRef,
  forwardRef,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import styled, { css } from 'styled-components';

import useFormField from 'design-system/components/FormControl/useFormField';
import { isDisabled, isFocused, isHovered, isNotDisabled } from 'design-system/lib/pseudoClass';
import useControlled from 'design-system/lib/useControlled';
import { focusMixin, hoverFocusMixin, hoverMixin } from 'design-system/mixins/hoverable';
import { TransitionDuration } from 'design-system/variables/transitions';

const Container = styled.div`
  position: relative;
  cursor: pointer;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  &${isDisabled} {
    cursor: default;
  }
`;

const CheckboxBox = styled.div<{ disabled?: boolean; checked?: boolean }>`
  cursor: pointer;
  display: flex;
  flex: 0 0 16px;
  justify-content: center;
  align-items: center;
  transition: box-shadow ${TransitionDuration.Fast};
  border-radius: 3px;
  height: ${Spacing[4]};
  width: ${Spacing[4]};
  box-sizing: border-box;
  border: 1px solid ${Color.BorderInputCheckbox};
  background: ${Color.BackgroundInputDefault};

  svg {
    color: ${Color.BorderIconInverse};
  }

  &${isDisabled} {
    cursor: default;
    background: ${Color.BackgroundInputDisabled};
    border-color: ${Color.BorderInputDisabled};
  }

  ${({ checked }) =>
    checked &&
    css`
      border-color: ${Color.BackgroundInputCheckboxChecked};
      background: ${Color.BackgroundInputCheckboxChecked};

      &${isDisabled} {
        border-color: ${Color.BackgroundInputCheckboxDisabled};
        background: ${Color.BackgroundInputCheckboxDisabled};
      }
    `}
`;

const CheckboxInput = styled.input`
  cursor: inherit;
  height: 16px;
  width: 16px;
  margin: 0;
  border: 0;
  opacity: 0;
  overflow: hidden;
  position: absolute;

  &${isHovered}${isNotDisabled} ~ ${CheckboxBox} {
    ${hoverMixin};
  }

  &${isFocused}${isNotDisabled} ~ ${CheckboxBox} {
    ${focusMixin('basic')};
  }

  &${isHovered}${isFocused}${isNotDisabled} ~ ${CheckboxBox} {
    ${hoverFocusMixin('basic')};
  }
`;

const Label = styled.label<{ disabled: boolean }>`
  cursor: inherit;
  padding: 0 ${Spacing[2]};
  color: ${Color.TextSecondary};
  ${Typography.TextMedium};

  /** Nudge text for visual alignment */
  position: relative;
  bottom: -1px;

  ${({ theme, disabled }) =>
    disabled &&
    css`
      cursor: default;
      color: ${theme.color.text.disabled};
    `}
`;

export interface CheckboxProps extends Omit<ComponentPropsWithoutRef<'input'>, 'value'> {
  /**
   * If not provided, a unique ID will be generated.
   */
  id?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  label?: ReactNode;
  disabled?: boolean;
  indeterminate?: boolean;
  checked?: boolean;
  value?: unknown;
  'data-test'?: string;
  containerProps?: ComponentPropsWithoutRef<'div'>;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  {
    className,
    disabled,
    indeterminate,
    onChange,
    checked: checkedProp,
    defaultChecked,
    id,
    label,
    'data-test': dataTest,
    containerProps,
    value,
    ...elemProps
  },
  ref,
) {
  const field = useFormField({ disabled });
  const [checked, setChecked] = useControlled({
    controlledValue: checkedProp,
    default: defaultChecked,
  });
  const generatedId = useMemo(() => uniqueId('pds_checkbox__'), []);
  const inputRef = useRef<HTMLInputElement>();

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  useImperativeHandle(ref, () => inputRef.current, [inputRef]);

  useEffect(() => {
    if (inputRef.current) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate, inputRef]);

  return (
    <Container
      className={className}
      data-test={dataTest}
      disabled={field.disabled}
      {...containerProps}
    >
      <CheckboxInput
        id={id || generatedId}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ref={ref || inputRef}
        data-test={dataTest && `${dataTest}-input`}
        disabled={field.disabled}
        type='checkbox'
        checked={checkedProp == null ? undefined : checked}
        defaultChecked={defaultChecked}
        onChange={(e) => {
          setChecked(e.target.checked);
          if (onChange) {
            onChange(e);
          }
        }}
        aria-checked={indeterminate ? 'mixed' : checked}
        value={value as any}
        {...elemProps}
      />
      <CheckboxBox
        disabled={field.disabled}
        checked={(checked != null && checked) || indeterminate}
        aria-checked={indeterminate ? 'mixed' : checked}
      >
        {indeterminate ? <InputDashIcon /> : checked ? <InputCheckIcon /> : null}
      </CheckboxBox>
      {label && (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        <Label htmlFor={id || generatedId} disabled={field.disabled}>
          {label}
        </Label>
      )}
    </Container>
  );
});

export default Checkbox;
